import { ReportPeriodWithLabel } from "@common/types/reports/types";
import {
    hasSingleValueColumn,
    SINGLE_COLUMN_LABEL_MAX_SIZE,
} from "@components/accounting/singleColumnReport";
import { ColumnDef } from "@components/spreadsheet/types";
import { LABEL_COLUMN } from "./reportTaskColumns";

// The bounds of the reports' own name and period columns
// (`profitLossViewConfig`, `balanceSheetViewConfig`).
const REPORT_LABEL_MIN_WIDTH = 240;
const REPORT_AMOUNT_MIN_WIDTH = 120;
const REPORT_AMOUNT_MAX_WIDTH = 200;

/** The Category column in the report look, as wide as the reports' own. */
export const REPORT_LOOK_LABEL_COLUMN: ColumnDef = {
    ...LABEL_COLUMN,
    width: 320,
};

/**
 * Period columns for the report look: headers align right over their
 * amounts, and the Total columns pin to the grid's right edge so they stay in
 * view while the other periods scroll. `columns` runs parallel to `periods`;
 * a report with nothing but totals keeps them unpinned.
 */
function toReportLookPeriodColumns({
    periods,
    columns,
}: {
    periods: readonly ReportPeriodWithLabel[];
    columns: ColumnDef[];
}): ColumnDef[] {
    const hasScrollingPeriods = periods.some((period) => !period.isTotal);
    return columns.map((column, idx) => ({
        ...column,
        headerAlign: "end",
        pinned:
            hasScrollingPeriods && periods[idx]?.isTotal
                ? "right"
                : column.pinned,
    }));
}

/**
 * The label column and period columns of a report task. With a single value
 * column, nothing pins and both columns take the report's bounds, so the grid
 * can center them (`SpreadsheetGrid` `centerColumns`) the way the report does.
 */
export function toReportLookColumns({
    periods,
    labelColumn,
    periodColumns,
}: {
    periods: ReportPeriodWithLabel[];
    labelColumn: ColumnDef;
    periodColumns: ColumnDef[];
}): ColumnDef[] {
    const lookPeriodColumns = toReportLookPeriodColumns({
        periods,
        columns: periodColumns,
    });
    if (!hasSingleValueColumn(periods)) {
        return [labelColumn, ...lookPeriodColumns];
    }
    return [
        {
            ...labelColumn,
            pinned: undefined,
            minSize: REPORT_LABEL_MIN_WIDTH,
            maxSize: SINGLE_COLUMN_LABEL_MAX_SIZE,
        },
        ...lookPeriodColumns.map((column) => ({
            ...column,
            pinned: undefined,
            minSize: REPORT_AMOUNT_MIN_WIDTH,
            maxSize: REPORT_AMOUNT_MAX_WIDTH,
        })),
    ];
}
